import { useCallback } from 'react';

import { AxiosResponse } from 'axios';

import { useSuspenseQuery } from '@tanstack/react-query';
import type { TypeGetRecruitNoticesTasksParams, TypeGetRecruitNoticesTasksResponse } from '~/api/recruitNotices';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';

export const TASKS_QUERY_KEY = 'tasks';

/** @description 모집중인 업무 목록 조회 */
export const useGetTasks = (params: TypeGetRecruitNoticesTasksParams) => {
  const handleResponse = useCallback((response: AxiosResponse<TypeGetRecruitNoticesTasksResponse>) => {
    return response.data.data;
  }, []);

  const { data } = useSuspenseQuery({
    queryKey: [TASKS_QUERY_KEY, params],
    queryFn: async () => {
      const endpoint = '/recruit-notices/tasks';
      return await axiosInstance.get<TypeGetRecruitNoticesTasksResponse>(endpoint, { params });
    },
    gcTime: 0,
    retry: 1,
    select: (response) => handleResponse(response),
  });

  return { data };
};

registerMockAPI<TypeGetRecruitNoticesTasksResponse>({
  method: 'get',
  endpoint: '/recruit-notices/tasks',
  label: '업무 선택/업무 목록 조회',
  scenarios: [
    {
      name: '업무 목록 (7개)',
      response: {
        code: '0000',
        message: '성공',
        data: [
          {
            task: {
              code: 'INBOUND',
              text: '입고',
              description: '검수가 완료된 상품을 진열대에 배치⋅보충해요.',
            },
            startDayparts: [
              { code: 'MORNING', text: '오전' },
              { code: 'AFTERNOON', text: '오후' },
              { code: 'NIGHT', text: '야간' },
            ],
          },
          {
            task: {
              code: 'OUTBOUND',
              text: '출고',
              description: '주문된 상품을 집품한 후, 주문자별로 분류하고 포장까지 진행하는 공정',
            },
            startDayparts: [
              { code: 'MORNING', text: '오전' },
              { code: 'AFTERNOON', text: '오후' },
            ],
          },
          {
            task: {
              code: 'HUB_SORTING',
              text: 'HUB/중간분류',
              description: '포장된 상품을 권역별로 분류하여 파래트에 적재해요.',
            },
            startDayparts: [{ code: 'MORNING', text: '오전' }],
          },
          {
            task: {
              code: 'INVENTORY_MANAGEMENT',
              text: '재고관리',
              description: '상품 재고 실사, 유통기한 관리 재고 정리, 상품 보관을 담당해요.',
            },
            startDayparts: [{ code: 'MORNING', text: '오전' }],
          },
          {
            task: {
              code: 'QUALITY_CONTROL',
              text: '품질관리(QC)',
              description: '입고 상품을 점검해 불량⋅파손 상품을 걸러내요.',
            },
            startDayparts: [{ code: 'MORNING', text: '오전' }],
          },
          {
            task: {
              code: 'INTEGRATED_RETURN',
              text: '통합회수(반품/회수)',
              description: '반품⋅회수된 상품의 상태를 확인하고 재처리해요.',
            },
            startDayparts: [{ code: 'MORNING', text: '오전' }],
          },
          {
            task: {
              code: 'INBOUND_AGENCY',
              text: '입고대행',
              description: '판매자 상품을 검수⋅분류 후 정리해요.',
            },
            startDayparts: [{ code: 'MORNING', text: '오전' }],
          },
        ],
      },
    },
    {
      name: '업무 목록 (단일)',
      response: {
        code: '0000',
        message: '성공',
        data: [
          {
            task: {
              code: 'INBOUND',
              text: '입고',
              description: '검수가 완료된 상품을 진열대에 배치⋅보충해요.',
            },
            startDayparts: [
              { code: 'MORNING', text: '오전' },
              { code: 'AFTERNOON', text: '오후' },
              { code: 'NIGHT', text: '야간' },
            ],
          },
        ],
      },
    },
    {
      name: '업무 목록 없음',
      response: {
        code: '0000',
        message: '성공',
        data: [],
      },
    },

    {
      name: '서버 에러',
      response: { code: '500', message: '서버 내부 오류가 발생했습니다.', data: [] },
      status: 500,
    },
  ],
});
